import { useId, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface TooltipProps extends Omit<ComponentPropsWithRef<"span">, "content"> {
  /** Contenu de l'infobulle. Texte court uniquement — jamais d'élément interactif. */
  content: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  children: ReactNode;
}

const SIDE_CLASSES = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
} as const;

/**
 * Infobulle, en CSS pur.
 *
 * Elle apparaît au survol ET au focus clavier (`group-focus-within`) : une
 * infobulle qui ne réagit qu'à la souris est inaccessible.
 *
 * Deux limites assumées de l'approche sans JavaScript :
 *   - pas de repositionnement automatique si l'infobulle sort de l'écran ;
 *   - elle est rognée par un parent en `overflow:hidden`.
 * Pour un cas exigeant (menu contextuel, popover riche), il faudra un vrai
 * moteur de positionnement — mais 95 % des usages tiennent ici.
 */
export function Tooltip({ className, content, side = "top", children, ...props }: TooltipProps) {
  const id = useId();

  return (
    <span className={cn("group relative inline-flex", className)} {...props}>
      {/* `aria-describedby` est ce qui fait lire l'infobulle par le lecteur d'écran. */}
      <span aria-describedby={id} className="inline-flex">
        {children}
      </span>

      <span
        role="tooltip"
        id={id}
        className={cn(
          "pointer-events-none absolute z-50 w-max max-w-56 rounded-md px-2 py-1",
          "bg-fg text-xs font-medium text-surface-raised shadow-lg",
          // Le délai est asymétrique, et c'est volontaire : il n'existe qu'à
          // l'apparition. Sans lui, traverser une barre d'outils déclenche une
          // cascade d'infobulles ; à la disparition, il donnerait l'impression
          // d'une interface qui traîne.
          "scale-95 opacity-0 delay-0",
          // Opacité + échelle : les deux seules propriétés en jeu sont celles
          // que le compositeur sait animer seul. Une infobulle qui apparaît
          // pendant un rendu React reste donc parfaitement fluide.
          "transform-gpu transition-[opacity,scale] duration-fast ease-entrance",
          "group-hover:scale-100 group-hover:opacity-100 group-hover:delay-300 group-hover:will-change-[opacity,transform]",
          "group-focus-within:scale-100 group-focus-within:opacity-100",
          SIDE_CLASSES[side],
        )}
      >
        {content}
      </span>
    </span>
  );
}
